import { Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { FieldTemplate, LayoutItem } from 'api/models';
import { produce } from 'immer';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { layoutSelector } from 'state';
import { dragState } from 'state/dragState';
import { insert, del, get } from 'object-path';
import { getIsPathPreceding } from '../utils';

export interface DropParams {
  layoutItem: LayoutItem;
  fromPath: string;
  field: FieldTemplate;
}

interface DropAreaProps {
  index: number;
  parentPath: string;
}

export function DropArea({ index, parentPath }: DropAreaProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [draggedField, setDraggedField] = useRecoilState(dragState);
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const succeedingItem = get(layout, `${parentPath}.${index}`);
  const layoutItem = get(layout, `${parentPath}.${index - 1}`);
  const isVisible =
    layoutItem?.id !== draggedField?.id &&
    succeedingItem?.id !== draggedField?.id;

  const [{ border, backgroundColor }, drop] = useDrop(() => ({
    accept: 'field-template',
    collect: monitor => ({
      border: monitor.isOver()
        ? `2px dashed ${theme.palette.divider}`
        : '0px solid transparent',
      boxShadow: monitor.isOver()
        ? 'inset 0px 0px 5px 1px rgba(0,0,0,0.25)'
        : 0,
      backgroundColor: monitor.isOver()
        ? theme.palette.action.hover
        : theme.palette.action.focus,
      canDrop: monitor.canDrop(),
    }),
    drop: ({ layoutItem, fromPath }: DropParams) => {
      setLayout(prevLayout =>
        produce(prevLayout, draft => {
          const isPreceding = getIsPathPreceding(
            fromPath,
            `${parentPath}.${index}`,
          );
          if (isPreceding) {
            insert(draft, parentPath, layoutItem, index);
            del(draft, fromPath);
          } else {
            del(draft, fromPath);
            insert(draft, parentPath, layoutItem, index);
          }
        }),
      );

      setDraggedField(undefined);
    },
  }));

  return (
    <Box
      ref={drop}
      sx={{
        backgroundColor,
        borderRadius: '5px',
        mb: 1,
        mt: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: draggedField !== undefined && isVisible ? '70px' : '0px',
        border,
        transition: 'all 0.7s',
      }}
    >
      {isVisible && (
        <Typography
          textAlign={'center'}
          fontSize={'small'}
          color={theme => theme.palette.text.secondary}
          sx={{
            opacity: draggedField !== undefined ? 1 : 0,
            transition: 'all 0.2s',
          }}
        >
          {t('forms.dragFieldHere')}
        </Typography>
      )}
    </Box>
  );
}
